import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Loader2, ChevronDown, ChevronRight, ChevronUp, Building2, Wallet, RefreshCw, AlertTriangle, Percent,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { CurrencyToggle } from '../components/CurrencyToggle';
import { PropertyCostEditor } from '../components/PropertyCostEditor';
import { PortfolioMetricChart } from '../components/PortfolioMetricChart';
import { usePortfolioPrefsStore, useEntityStore } from '../store';
import { convertAmount, currencyLabel } from '../lib/currencyConvert';
import { portfolioAPI, diagnosticsAPI, propertyCostsAPI } from '../lib/api';
import { DetectedRecurringCosts } from '../components/DetectedRecurringCosts';
import { MonthlyOutgoings } from '../components/MonthlyOutgoings';
import { computeDerivedMetrics } from '../lib/portfolioDerived';
import { formatCurrency } from '../lib/utils';
import { mergeAllMonths, monthStatementTotal } from '../lib/cleaningListDerive';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';
import { computeCostRevenueShare, formatPct, pctOf } from '../lib/costRevenueShare';
import { CostRevenueShare } from '../components/CostRevenueShare';
import { monthlyCostsByCurrency } from '../lib/costRowLabel';

const currentYear = new Date().getFullYear();

const REGION_LABELS = { uk: '🇬🇧 UK', fr: '🇫🇷 France' };
const TENURE_LABELS = { leased: 'Leased', owned: 'Owned' };
const TENURE_COLORS = {
  leased: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  owned: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CLEANING_REGION_TABS = [
  { id: 'all', label: 'All' },
  { id: 'uk', label: '🇬🇧 UK' },
  { id: 'fr', label: '🇫🇷 France' },
];

// Sign convention: Overbilled = CWC charged MORE than the 17% expected
// (raises the owner's cost); Underbilled = CWC charged LESS (current CWC
// situation for most properties).
const COMMISSION_EPSILON = (expected) => Math.max(0.50, 0.005 * Math.abs(expected));

function commissionStatus(charged, expected) {
  if (!expected || expected === 0) {
    if (charged > 0) return { label: 'No basis', gap: charged, variant: 'warning' };
    return { label: '\u2014', gap: 0, variant: 'neutral' };
  }
  const gap = charged - expected;
  const eps = COMMISSION_EPSILON(expected);
  if (Math.abs(gap) <= eps) return { label: 'Reconciled', gap: 0, variant: 'reconciled' };
  if (gap > 0) return { label: 'Overbilled', gap, variant: 'overbilled' };
  return { label: 'Underbilled', gap: -gap, variant: 'underbilled' };
}

const STATUS_BADGE_STYLES = {
  reconciled: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400',
  overbilled: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  underbilled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400',
  warning: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  neutral: 'bg-muted text-muted-foreground',
};


// ── Main page ──

export function CostsPage() {
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const entities = useEntityStore((s) => s.entities);
  const [searchParams] = useSearchParams();
  const focusPropertyId = searchParams.get('focus');
  // Deep-link anchor from the Bank Transactions "Reconciled By" chip
  // (see frontend/src/lib/reconciliationArtifact.js): a rent/recurring-cost
  // reconciliation opens this page anchored to its property AND the year
  // its month falls in, so the focused property's monthly breakdown is
  // already showing the right period once scrolled into view.
  const [year, setYear] = useState(() => {
    const fromUrl = parseInt(searchParams.get('year'), 10);
    return Number.isFinite(fromUrl) ? fromUrl : currentYear;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProperties, setExpandedProperties] = useState(
    () => new Set(focusPropertyId ? [focusPropertyId] : [])
  );
  const focusRef = useRef(null);

  // Cleaning list state (year-based, portfolio-wide)
  const [cleaningYearData, setCleaningYearData] = useState(null);
  const [cleaningLoading, setCleaningLoading] = useState(false);
  const [cleaningRegion, setCleaningRegion] = useState('all');
  const [cleaningMonthFilter, setCleaningMonthFilter] = useState('all');
  const [cleaningPropertyFilter, setCleaningPropertyFilter] = useState('all');
  const [expandedCleaningMonths, setExpandedCleaningMonths] = useState(() => new Set());

  // Commission list state (year-based, portfolio-wide)
  const [commissionYearData, setCommissionYearData] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [commissionRegion, setCommissionRegion] = useState('all');
  const [commissionMonthFilter, setCommissionMonthFilter] = useState('all');
  const [expandedCommissionMonths, setExpandedCommissionMonths] = useState(() => new Set());
  const [commissionRecomputing, setCommissionRecomputing] = useState(false);

  // Maintenance list state (year-based, portfolio-wide)
  const [maintenanceYearData, setMaintenanceYearData] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [maintenanceRegion, setMaintenanceRegion] = useState('all');
  const [maintenanceMonthFilter, setMaintenanceMonthFilter] = useState('all');
  const [maintenancePropertyFilter, setMaintenancePropertyFilter] = useState('all');
  const [expandedMaintenanceMonths, setExpandedMaintenanceMonths] = useState(() => new Set());
  const [maintenanceRecomputing, setMaintenanceRecomputing] = useState(false);

  // Monthly outgoings: all entities (including br_ltd for entity-scoped opex).
  // Uses e.type (the EntityType slug, e.g. "br_ltd") as the id, NOT e.id
  // (which is a UUID). The monthly-outgoings backend validates entity_id
  // against ENTITY_IDS (slug set), so the selector must emit slugs.
  const outgoingsEntities = useMemo(() => {
    if (!entities || entities.length === 0) return [];
    return entities
      .filter((e) => e.type && e.type !== 'unassigned')
      .map((e) => ({ id: e.type, name: e.canonical_name || e.name || e.type }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entities]);

  // Missing-costs diagnostic state
  const [missingCosts, setMissingCosts] = useState(null);
  const [missingCostsLoading, setMissingCostsLoading] = useState(false);
  const [missingCostsExpanded, setMissingCostsExpanded] = useState(() => new Set());

  // Edinburgh Visitor Levy: already out of UK revenue; memo note only (Fan, 2026-10-24)
  const [visitorLevy, setVisitorLevy] = useState(null);
  useEffect(() => {
    let alive = true;
    portfolioAPI.getVisitorLevy(year)
      .then((r) => { if (alive) setVisitorLevy(r); })
      .catch(() => { if (alive) setVisitorLevy(null); });
    return () => { alive = false; };
  }, [year]);

  const fetchMissingCosts = useCallback(async () => {
    setMissingCostsLoading(true);
    try {
      const { data: result } = await propertyCostsAPI.getMissingCosts(year);
      setMissingCosts(result);
    } catch (err) {
      // Non-critical diagnostic; don't toast on error, just clear
      setMissingCosts(null);
    } finally {
      setMissingCostsLoading(false);
    }
  }, [year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await portfolioAPI.getDataset({ year });
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCleaningYear = async () => {
    setCleaningLoading(true);
    setCleaningYearData(null);
    setCleaningMonthFilter('all');
    setCleaningPropertyFilter('all');
    setExpandedCleaningMonths(new Set());
    try {
      const { data: result } = await diagnosticsAPI.cleaningVerificationYear({
        entity_id: 'property_portfolio',
        year,
      });
      setCleaningYearData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCleaningLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [year]);

  // Fetch cleaning year data when year changes (portfolio-wide)
  useEffect(() => {
    fetchCleaningYear();
  }, [year]);

  // Fetch missing-costs diagnostic when year changes
  useEffect(() => {
    fetchMissingCosts();
  }, [fetchMissingCosts]);

  const fetchCommissionYear = async () => {
    setCommissionLoading(true);
    setCommissionYearData(null);
    setCommissionMonthFilter('all');
    setExpandedCommissionMonths(new Set());
    try {
      const { data: result } = await diagnosticsAPI.commissionVerificationYear({
        entity_id: 'property_portfolio',
        year,
      });
      setCommissionYearData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCommissionLoading(false);
    }
  };

  const recomputeCommission = async () => {
    setCommissionRecomputing(true);
    try {
      const { data: result } = await diagnosticsAPI.commissionVerificationReconcile();
      const recomputed = result?.recomputed ?? 0;
      toast.success(`Recomputed ${recomputed} verification result${recomputed === 1 ? '' : 's'}`);
      await fetchCommissionYear();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCommissionRecomputing(false);
    }
  };

  // Fetch commission year data when year changes (portfolio-wide)
  useEffect(() => {
    fetchCommissionYear();
  }, [year]);

  const fetchMaintenanceYear = async () => {
    setMaintenanceLoading(true);
    setMaintenanceYearData(null);
    setMaintenanceMonthFilter('all');
    setMaintenancePropertyFilter('all');
    setExpandedMaintenanceMonths(new Set());
    try {
      const { data: result } = await diagnosticsAPI.maintenanceVerificationYear({
        entity_id: 'property_portfolio',
        year,
      });
      setMaintenanceYearData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const recomputeMaintenance = async () => {
    setMaintenanceRecomputing(true);
    try {
      const { data: result } = await diagnosticsAPI.maintenanceVerificationReconcile();
      const recomputed = result?.recomputed ?? 0;
      toast.success(`Recomputed ${recomputed} maintenance verification result${recomputed === 1 ? '' : 's'}`);
      await fetchMaintenanceYear();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMaintenanceRecomputing(false);
    }
  };

  // Fetch maintenance year data when year changes (portfolio-wide)
  useEffect(() => {
    fetchMaintenanceYear();
  }, [year]);

  // Scroll to focused property once data loads
  useEffect(() => {
    if (!loading && focusPropertyId && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, focusPropertyId]);

  const toggleProperty = (propertyId) => {
    setExpandedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
  };

  // Derive cost aggregates from the portfolio dataset
  const derived = useMemo(() => {
    if (!data) return null;
    return computeDerivedMetrics(data);
  }, [data]);

  const { propertiesByRegion, costOverview } = useMemo(() => {
    if (!derived) return { propertiesByRegion: {}, costOverview: null };
    const { costsByPropMonth, allCostsByPropMonth } = derived;

    const byRegion = {};
    for (const prop of data.properties || []) {
      const region = prop.region || 'uk';
      if (!byRegion[region]) byRegion[region] = [];

      let pnlTotal = 0;
      let cashTotal = 0;
      for (let m = 1; m <= 12; m++) {
        const key = `${prop.property_id}|${year}-${String(m).padStart(2, '0')}`;
        pnlTotal += costsByPropMonth[key] || 0;
        cashTotal += allCostsByPropMonth[key] || 0;
      }

      const propCosts = (data.recurring_costs || []).filter(
        (c) => c.property_id === prop.property_id
      );
      const costTypeSet = new Set(propCosts.map((c) => c.cost_type));

      byRegion[region].push({
        ...prop,
        ytd_pnl_costs: pnlTotal,
        ytd_cash_costs: cashTotal,
        cost_type_count: costTypeSet.size,
        monthly_costs_by_ccy: monthlyCostsByCurrency(propCosts),
      });
    }

    for (const region of Object.keys(byRegion)) {
      byRegion[region].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    const overview = {};
    for (const [region, props] of Object.entries(byRegion)) {
      const currency = region === 'fr' ? 'EUR' : 'GBP';
      overview[region] = {
        property_count: props.length,
        total_pnl_costs: props.reduce((s, p) => s + p.ytd_pnl_costs, 0),
        total_cash_costs: props.reduce((s, p) => s + p.ytd_cash_costs, 0),
        currency,
      };
    }

    return { propertiesByRegion: byRegion, costOverview: overview };
  }, [derived, data, year]);

  // Costs as % of revenue (Booking Volume), cash view incl. full mortgage
  // instalments (operator, 2026-10-15). Single source for the cards, the
  // chart and the share tables below so their totals agree.
  const costShare = useMemo(() => {
    if (!derived || !data) return null;
    return computeCostRevenueShare({
      monthlyByRegion: derived.monthlyByRegion,
      recurringCosts: data.recurring_costs,
      properties: data.properties,
      year,
    });
  }, [derived, data, year]);

  // Build cost monthlySeries for PortfolioMetricChart from the same share
  // computation (so the chart includes mortgage repayments, cash view).
  const costMonthlySeries = useMemo(() => {
    if (costShare) {
      return costShare.months.map((m) => ({
        month: MONTHS_SHORT[m.index],
        ukNative: m.uk?.costs || 0,
        frNative: m.fr?.costs || 0,
      }));
    }
    if (!derived) return [];
    const { monthlyByRegion } = derived;
    if (!monthlyByRegion) return [];

    const now = new Date();
    const series = [];
    for (let m = 0; m < 12; m++) {
      // Only include past/current months for the selected year
      if (year === now.getFullYear() && m + 1 > now.getMonth() + 1) break;
      if (year > now.getFullYear()) break;

      const uk = monthlyByRegion.uk?.[m];
      const fr = monthlyByRegion.fr?.[m];
      series.push({
        month: MONTHS_SHORT[m],
        ukNative: uk?.total_costs_gbp || 0,
        frNative: fr?.total_costs_eur || 0,
      });
    }
    return series;
  }, [derived, year, costShare]);

  const eurGbpRate = parseFloat(data?.fx_rates?.EUR_GBP) || 0.85;
  const cv = (value, fromCurrency) => convertAmount(value, fromCurrency, displayCurrency, eurGbpRate);

  // Derive the merged cleaning rows from year data
  const cleaningDerived = useMemo(() => {
    const monthResults = cleaningYearData?.months || [];
    const allRows = mergeAllMonths(monthResults);

    // Month totals from statement lines (source of truth, not row sums).
    // Accumulate across entities for the same month (portfolio scope).
    const monthTotals = {};
    for (const mr of monthResults) {
      monthTotals[mr.month] = (monthTotals[mr.month] || 0) + monthStatementTotal(mr);
    }

    // Distinct properties for filter
    const propertySet = new Map();
    for (const r of allRows) {
      if (r.property_id && !propertySet.has(r.property_id)) {
        propertySet.set(r.property_id, r.property_name);
      }
    }
    const properties = Array.from(propertySet.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Distinct months that have data
    const monthsWithData = [...new Set(allRows.map(r => r.month))].sort();

    return { allRows, monthTotals, properties, monthsWithData };
  }, [cleaningYearData]);

  // Apply filters (region + month + property)
  const cleaningFiltered = useMemo(() => {
    let rows = cleaningDerived.allRows;
    if (cleaningRegion !== 'all') {
      rows = rows.filter(r => r.region === cleaningRegion);
    }
    if (cleaningMonthFilter !== 'all') {
      rows = rows.filter(r => r.month === cleaningMonthFilter);
    }
    if (cleaningPropertyFilter !== 'all') {
      rows = rows.filter(r => r.property_id === cleaningPropertyFilter);
    }
    return rows;
  }, [cleaningDerived.allRows, cleaningRegion, cleaningMonthFilter, cleaningPropertyFilter]);

  // Group filtered rows by month, then by property within each month
  const cleaningGrouped = useMemo(() => {
    const byMonth = {};
    for (const row of cleaningFiltered) {
      if (!byMonth[row.month]) byMonth[row.month] = [];
      byMonth[row.month].push(row);
    }
    const months = Object.keys(byMonth).sort();
    return months.map(month => {
      const monthRows = byMonth[month];
      const byProp = {};
      for (const row of monthRows) {
        const pid = row.property_id || '(unknown)';
        if (!byProp[pid]) byProp[pid] = { property_id: pid, property_name: row.property_name, rows: [] };
        byProp[pid].rows.push(row);
      }
      const propGroups = Object.values(byProp).sort((a, b) => a.property_name.localeCompare(b.property_name));
      const monthSubtotal = cleaningDerived.monthTotals[month] || 0;
      // Filtered subtotal: sum of allocated per-clean amounts for shown rows
      const filteredSubtotal = Math.round(monthRows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
      return { month, propGroups, monthSubtotal, filteredSubtotal, cleanCount: monthRows.length };
    });
  }, [cleaningFiltered, cleaningDerived.monthTotals]);

  // Period total (sum of filtered month subtotals)
  const cleaningPeriodTotal = useMemo(() => {
    return Math.round(cleaningGrouped.reduce((s, mg) => s + mg.filteredSubtotal, 0) * 100) / 100;
  }, [cleaningGrouped]);

  // ── Commission derivation ──
  const commissionDerived = useMemo(() => {
    const monthResults = commissionYearData?.months || [];
    // Flatten: one row per line (per statement per property per month)
    const allRows = [];
    for (const mr of monthResults) {
      const entityId = mr.entity_id || '';
      const entityName = mr.entity_name || entityId;
      const region = mr.region || 'uk';
      for (const line of (mr.lines || [])) {
        const htAmount = parseFloat(line.amount || 0);
        const ttcAmount = parseFloat(line.amount_ttc || 0);
        const expectedHt = parseFloat(line.expected_ht || 0);
        const netRevenue = parseFloat(line.net_revenue || 0);
        allRows.push({
          month: mr.month,
          entity_id: entityId,
          entity_name: entityName,
          region,
          property_ref: line.property_ref || '(unknown)',
          property_name: line.property_name_raw || line.property_ref || '(unknown)',
          amount_ht: htAmount,
          amount_ttc: ttcAmount,
          expected_ht: expectedHt,
          net_revenue: netRevenue,
          booking_count: line.booking_count || 0,
          agency: line.agency || '',
        });
      }
    }

    // Distinct months with data
    const monthsWithData = [...new Set(allRows.map(r => r.month))].sort();

    // Distinct properties for filter
    const propertySet = new Map();
    for (const r of allRows) {
      if (r.property_ref && !propertySet.has(r.property_ref)) {
        propertySet.set(r.property_ref, r.property_name);
      }
    }
    const properties = Array.from(propertySet.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { allRows, monthsWithData, properties };
  }, [commissionYearData]);

  // Apply commission filters
  const commissionFiltered = useMemo(() => {
    let rows = commissionDerived.allRows;
    if (commissionRegion !== 'all') {
      rows = rows.filter(r => r.region === commissionRegion);
    }
    if (commissionMonthFilter !== 'all') {
      rows = rows.filter(r => r.month === commissionMonthFilter);
    }
    return rows;
  }, [commissionDerived.allRows, commissionRegion, commissionMonthFilter]);

  // Group commission by month
  const commissionGrouped = useMemo(() => {
    const byMonth = {};
    for (const row of commissionFiltered) {
      if (!byMonth[row.month]) byMonth[row.month] = [];
      byMonth[row.month].push(row);
    }
    return Object.keys(byMonth).sort().map(month => {
      const rows = byMonth[month];
      const subtotalHt = Math.round(rows.reduce((s, r) => s + r.amount_ht, 0) * 100) / 100;
      const subtotalExpected = Math.round(rows.reduce((s, r) => s + r.expected_ht, 0) * 100) / 100;
      return { month, rows, subtotalHt, subtotalExpected };
    });
  }, [commissionFiltered]);

  const commissionPeriodTotal = useMemo(() => {
    const charged = Math.round(commissionGrouped.reduce((s, mg) => s + mg.subtotalHt, 0) * 100) / 100;
    const expected = Math.round(commissionGrouped.reduce((s, mg) => s + mg.subtotalExpected, 0) * 100) / 100;
    return { charged, expected };
  }, [commissionGrouped]);

  // ── Maintenance derivation ──
  const maintenanceDerived = useMemo(() => {
    const monthResults = maintenanceYearData?.months || [];
    const allRows = [];
    for (const mr of monthResults) {
      const entityId = mr.entity_id || '';
      const entityName = mr.entity_name || entityId;
      const region = mr.region || 'uk';
      for (const line of (mr.lines || [])) {
        const amount = parseFloat(line.amount || 0);
        allRows.push({
          month: mr.month,
          entity_id: entityId,
          entity_name: entityName,
          region,
          property_ref: line.property_ref || '(unknown)',
          property_name: line.property_name_raw || line.property_ref || '(unknown)',
          date: line.date || '',
          supplier: line.supplier || '',
          description: line.description || '',
          account: line.account || '',
          amount,
          agency: line.agency || '',
          statement_id: line.statement_id || '',
        });
      }
    }
    // Distinct months with data
    const monthsWithData = [...new Set(allRows.map(r => r.month))].sort();
    // Distinct properties for filter
    const propertySet = new Map();
    for (const r of allRows) {
      if (r.property_ref && !propertySet.has(r.property_ref)) {
        propertySet.set(r.property_ref, r.property_name);
      }
    }
    const properties = Array.from(propertySet.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));

    return { allRows, monthsWithData, properties };
  }, [maintenanceYearData]);

  // Apply maintenance filters
  const maintenanceFiltered = useMemo(() => {
    let rows = maintenanceDerived.allRows;
    if (maintenanceRegion !== 'all') {
      rows = rows.filter(r => r.region === maintenanceRegion);
    }
    if (maintenanceMonthFilter !== 'all') {
      rows = rows.filter(r => r.month === maintenanceMonthFilter);
    }
    if (maintenancePropertyFilter !== 'all') {
      rows = rows.filter(r => r.property_ref === maintenancePropertyFilter);
    }
    return rows;
  }, [maintenanceDerived.allRows, maintenanceRegion, maintenanceMonthFilter, maintenancePropertyFilter]);

  // Group maintenance by month, then by property
  const maintenanceGrouped = useMemo(() => {
    const byMonth = {};
    for (const row of maintenanceFiltered) {
      if (!byMonth[row.month]) byMonth[row.month] = [];
      byMonth[row.month].push(row);
    }
    const months = Object.keys(byMonth).sort();
    return months.map(month => {
      const monthRows = byMonth[month];
      const byProp = {};
      for (const row of monthRows) {
        const pid = row.property_ref || '(unknown)';
        if (!byProp[pid]) byProp[pid] = { property_ref: pid, property_name: row.property_name, rows: [] };
        byProp[pid].rows.push(row);
      }
      const propGroups = Object.values(byProp).sort((a, b) => a.property_name.localeCompare(b.property_name));
      const subtotal = Math.round(monthRows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
      return { month, propGroups, subtotal, lineCount: monthRows.length };
    });
  }, [maintenanceFiltered]);

  const maintenancePeriodTotal = useMemo(() => {
    return Math.round(maintenanceGrouped.reduce((s, mg) => s + mg.subtotal, 0) * 100) / 100;
  }, [maintenanceGrouped]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Costs</h1>
            <p className="text-muted-foreground mt-1">
              Cost model overview, property cost entry, and cleaning verification
            </p>
          </div>
          <div className="flex items-center gap-3">
            <CurrencyToggle />
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <Tabs defaultValue={focusPropertyId ? 'cost-entry' : 'overall'}>
          <TabsList>
            <TabsTrigger value="overall">Overall</TabsTrigger>
            <TabsTrigger value="cost-entry">Cost Entry</TabsTrigger>
            <TabsTrigger value="cleaning">Cleaning</TabsTrigger>
            {/* Display label is "Management Fee" (the P&L bucket name). The
                underlying cost_type stays management_commission; see four-fee-meanings
                in CLAUDE.md. If BR's own management_fee revenue ever shares a surface
                with this, disambiguate: "Management Fee (paid)" vs "Management Fee (earned)". */}
            <TabsTrigger value="commission">Management Fee</TabsTrigger>
            <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
            <TabsTrigger value="detected">Detected</TabsTrigger>
            <TabsTrigger value="outgoings">Monthly Outgoings</TabsTrigger>
          </TabsList>

          {/* ═══════════════ OVERALL TAB ═══════════════ */}
          <TabsContent value="overall" className="space-y-6">

            {/* Cost bar chart (mirrors Revenue page) */}
            <PortfolioMetricChart
              cards={costShare ? (() => {
                const totalCosts = cv(costShare.regions.uk.total, 'GBP') + cv(costShare.regions.fr.total, 'EUR');
                const totalRev = cv(costShare.regions.uk.revenue, 'GBP') + cv(costShare.regions.fr.revenue, 'EUR');
                return [
                  {
                    label: `Total Costs (${displayCurrency} approx)`,
                    value: formatCurrency(totalCosts, displayCurrency),
                    icon: Wallet,
                    color: 'bg-orange-100 dark:bg-orange-900/30',
                    sub: 'text-orange-600 dark:text-orange-400',
                  },
                  {
                    label: 'Costs as % of revenue',
                    value: formatPct(pctOf(totalCosts, totalRev)),
                    icon: Percent,
                    color: 'bg-orange-100 dark:bg-orange-900/30',
                    sub: 'text-orange-600 dark:text-orange-400',
                  },
                  {
                    label: 'UK costs as % of revenue',
                    value: formatPct(costShare.regions.uk.pct),
                    icon: Percent,
                    color: 'bg-orange-100 dark:bg-orange-900/30',
                    sub: 'text-orange-600 dark:text-orange-400',
                  },
                  {
                    label: 'France costs as % of revenue',
                    value: formatPct(costShare.regions.fr.pct),
                    icon: Percent,
                    color: 'bg-orange-100 dark:bg-orange-900/30',
                    sub: 'text-orange-600 dark:text-orange-400',
                  },
                ];
              })() : []}
              monthlySeries={costMonthlySeries}
              forecastByMonth={null}
              title="Cost Model Trend"
              ukLabel="UK Costs"
              frLabel="FR Costs"
              displayCurrency={displayCurrency}
              eurGbpRate={eurGbpRate}
              year={year}
              portfolioRegion="all"
            />

            <CostRevenueShare share={costShare} displayCurrency={displayCurrency} cv={cv} year={year} />

            {visitorLevy?.total > 0 && (
              <p className="text-xs text-muted-foreground -mt-3" data-testid="visitor-levy-note">
                UK revenue here is without the Edinburgh visitor levy: {formatCurrency(cv(visitorLevy.total, 'GBP'), displayCurrency)} in {year}.
                It goes to the council, so it is neither our revenue nor our cost.
              </p>
            )}

            {/* Missing-costs diagnostic */}
            {missingCosts && missingCosts.properties_with_gaps > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="h-4 w-4" />
                    Missing Cost Types ({missingCosts.properties_with_gaps} of {missingCosts.properties_scanned} properties)
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 pb-3">
                  <p className="text-xs text-muted-foreground mb-3">
                    Properties with revenue in {year} missing a core running cost: rent, utilities, insurance and council tax (UK); also cleaning, maintenance, copropriété and taxe foncière (France). CWC fees, cleaning and repairs come from the statements; optional types (deposit, travaux, loans) are not listed.
                    This worklist is monitored by the cost-gaps daily check on the <Link to="/attention" className="underline hover:text-foreground">Attention</Link> page.
                  </p>
                  <div className="space-y-1">
                    {missingCosts.report.map((prop) => {
                      const isExp = missingCostsExpanded.has(prop.property_id);
                      return (
                        <div key={prop.property_id} className="border rounded-md">
                          <button
                            onClick={() => setMissingCostsExpanded((prev) => {
                              const next = new Set(prev);
                              next.has(prop.property_id) ? next.delete(prop.property_id) : next.add(prop.property_id);
                              return next;
                            })}
                            className="w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-muted/50"
                          >
                            <span className="text-xs font-medium">{prop.property_name || prop.property_id}</span>
                            <span className="flex items-center gap-2">
                              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                                {prop.missing_count} missing
                              </Badge>
                              {isExp ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </span>
                          </button>
                          {isExp && (
                            <div className="px-3 pb-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                              {prop.cost_types_missing.map((ct) => (
                                <span key={ct.cost_type} className="text-[10px] text-muted-foreground">
                                  {ct.display_label}
                                  <span className="ml-1 opacity-60">({ct.bucket})</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

          </TabsContent>

          {/* ═══════════════ COST ENTRY TAB ═══════════════ */}
          <TabsContent value="cost-entry" className="space-y-6">
            {Object.entries(propertiesByRegion).map(([region, properties]) => {
              const currency = region === 'fr' ? 'EUR' : 'GBP';
              return (
                <Card key={region}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      {REGION_LABELS[region] || region} Properties
                      <Badge variant="secondary" className="ml-1">{properties.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <TableHead>Property</TableHead>
                            <TableHead className="text-center">Tenure</TableHead>
                            <TableHead className="text-center">Cost types</TableHead>
                            <TableHead className="text-right" title="Live cost lines per month: quarterly / 3, annual / 12; per-stay cleaning not included">Monthly costs</TableHead>
                            <TableHead className="text-right">P&L costs (YTD)</TableHead>
                            <TableHead className="text-right">Cash costs (YTD)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {properties.map((prop) => {
                            const isExpanded = expandedProperties.has(prop.property_id);
                            const isFocused = prop.property_id === focusPropertyId;
                            return (
                              <React.Fragment key={prop.property_id}>
                                <TableRow
                                  ref={isFocused ? focusRef : undefined}
                                  className="cursor-pointer hover:bg-muted/50"
                                  onClick={() => toggleProperty(prop.property_id)}
                                >
                                  <TableCell className="w-8 px-2">
                                    {isExpanded ? (
                                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </TableCell>
                                  <TableCell className="font-medium">{prop.name || prop.property_id}</TableCell>
                                  <TableCell className="text-center">
                                    {prop.tenure ? (
                                      <span className="inline-flex items-center gap-1">
                                        <Badge variant="secondary" className={TENURE_COLORS[prop.tenure] || ''}>
                                          {TENURE_LABELS[prop.tenure] || prop.tenure}
                                        </Badge>
                                        {prop.tenure_derived && (
                                          <span className="text-[10px] text-muted-foreground" title="Derived from property region (editable)">auto</span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">Not set</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center tabular-nums">{prop.cost_type_count}</TableCell>
                                  <TableCell className="text-right tabular-nums font-mono" data-testid={`cost-entry-monthly-${prop.property_id}`}>
                                    {(() => {
                                      const total = Object.entries(prop.monthly_costs_by_ccy || {})
                                        .reduce((s, [ccy, v]) => s + (cv(v, ccy) || 0), 0);
                                      return total > 0 ? formatCurrency(total, displayCurrency) : '\u2014';
                                    })()}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums font-mono">
                                    {prop.ytd_pnl_costs > 0 ? formatCurrency(cv(prop.ytd_pnl_costs, currency), displayCurrency) : '\u2014'}
                                  </TableCell>
                                  <TableCell className="text-right tabular-nums font-mono">
                                    {prop.ytd_cash_costs > 0 ? formatCurrency(cv(prop.ytd_cash_costs, currency), displayCurrency) : '\u2014'}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={7} className="bg-muted/20 p-4">
                                      {/* raw provider id, not the canonical property_id -- see _resolve_property */}
                                      <PropertyCostEditor
                                        propertyId={prop.primary_property_id || prop.property_id}
                                        propertyCurrency={currency}
                                        initialTenure={prop.tenure}
                                        tenureDerived={prop.tenure_derived}
                                      />
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {properties.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                No properties in this region.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* ═══════════════ CLEANING TAB ═══════════════ */}
          <TabsContent value="cleaning" className="space-y-6">

            {/* Scope header + period total */}
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Billed cleaning actuals from management statements, reflected in P&amp;L Cleaning column.
                  </p>
                  {cleaningPeriodTotal > 0 && (
                    <span className="text-sm font-semibold tabular-nums font-mono">
                      Total: {formatCurrency(cv(cleaningPeriodTotal, 'GBP'), displayCurrency)}
                      {displayCurrency !== 'GBP' && (
                        <span className="text-xs text-muted-foreground ml-1">({currencyLabel('GBP', displayCurrency)})</span>
                      )}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Region tabs + filters */}
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Region</label>
                <div className="flex gap-1">
                  {CLEANING_REGION_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setCleaningRegion(tab.id)}
                      className={`h-9 px-3 text-sm rounded-md border ${
                        cleaningRegion === tab.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-input hover:bg-muted'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Month</label>
                <select
                  value={cleaningMonthFilter}
                  onChange={(e) => setCleaningMonthFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="all">All months</option>
                  {cleaningDerived.monthsWithData.map((m) => {
                    const mn = parseInt(m.split('-')[1], 10);
                    return <option key={m} value={m}>{MONTHS_SHORT[mn - 1]} {year}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Property</label>
                <select
                  value={cleaningPropertyFilter}
                  onChange={(e) => setCleaningPropertyFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="all">All properties</option>
                  {cleaningDerived.properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {cleaningLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : cleaningGrouped.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Clean date</TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Matched booking</TableHead>
                        <TableHead className="text-right">Amount ({currencyLabel('GBP', displayCurrency)})</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cleaningGrouped.map((mg) => {
                        const mn = parseInt(mg.month.split('-')[1], 10);
                        const monthLabel = `${MONTHS_SHORT[mn - 1]} ${mg.month.split('-')[0]}`;
                        const isExpanded = expandedCleaningMonths.has(mg.month);
                        const toggleMonth = () => {
                          setExpandedCleaningMonths((prev) => {
                            const next = new Set(prev);
                            next.has(mg.month) ? next.delete(mg.month) : next.add(mg.month);
                            return next;
                          });
                        };

                        return (
                          <React.Fragment key={mg.month}>
                            {/* Month summary row */}
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50 font-medium"
                              onClick={toggleMonth}
                            >
                              <TableCell className="w-8 px-2">
                                {isExpanded
                                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                }
                              </TableCell>
                              <TableCell colSpan={5}>
                                {monthLabel}
                                <span className="ml-2 text-xs text-muted-foreground font-normal">
                                  {mg.cleanCount} clean{mg.cleanCount !== 1 ? 's' : ''}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums">
                                {formatCurrency(cv(mg.filteredSubtotal, 'GBP'), displayCurrency)}
                              </TableCell>
                            </TableRow>

                            {/* Expanded: per-property groups and rows */}
                            {isExpanded && mg.propGroups.map((pg) => {
                              const propSubtotal = Math.round(pg.rows.reduce((s, r) => s + r.amount, 0) * 100) / 100;
                              return (
                                <React.Fragment key={`${mg.month}-${pg.property_id}`}>
                                  {/* Property subtotal row */}
                                  {mg.propGroups.length > 1 && (
                                    <TableRow className="bg-muted/30">
                                      <TableCell></TableCell>
                                      <TableCell colSpan={5} className="text-xs font-medium">
                                        {pg.property_name}
                                        <span className="ml-2 text-muted-foreground font-normal">
                                          {pg.rows.length} clean{pg.rows.length !== 1 ? 's' : ''}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right font-mono text-xs tabular-nums">
                                        {formatCurrency(cv(propSubtotal, 'GBP'), displayCurrency)}
                                      </TableCell>
                                    </TableRow>
                                  )}

                                  {/* Individual clean rows */}
                                  {pg.rows.map((row, ri) => (
                                    <TableRow key={`${mg.month}-${pg.property_id}-${ri}`} className="bg-muted/10 text-xs">
                                      <TableCell></TableCell>
                                      <TableCell className="font-mono py-1.5">
                                        {row.clean_date || '-'}
                                      </TableCell>
                                      <TableCell className="py-1.5">
                                        {row.property_name}
                                      </TableCell>
                                      <TableCell className="py-1.5 text-muted-foreground">
                                        {row.entity_name}
                                      </TableCell>
                                      <TableCell className="py-1.5">
                                        <CleanTypeBadge classification={row.classification} />
                                      </TableCell>
                                      <TableCell className="py-1.5">
                                        <CleanMatchCell row={row} />
                                      </TableCell>
                                      <TableCell className="text-right font-mono py-1.5 tabular-nums">
                                        {formatCurrency(cv(row.amount, 'GBP'), displayCurrency)}
                                        {row.is_bundled && (
                                          <span className="ml-1 text-[10px] text-orange-500" title={`Bundle of ${row.bundle_size}, total ${formatCurrency(row.source_amount, 'GBP')}`}>
                                            (1/{row.bundle_size})
                                          </span>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground text-sm">
                    {cleaningRegion !== 'all'
                      ? `No cleaning statements for ${cleaningRegion === 'uk' ? 'UK' : 'France'} properties in ${year}.`
                      : `No cleaning data across portfolio properties in ${year}.`
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ═══════════════ COMMISSION TAB ═══════════════ */}
          <TabsContent value="commission" className="space-y-6">

            {/* Scope header + period total + aggregate status */}
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-muted-foreground">
                    Management fee from statements vs 17% contractual expected. Charged amount enters P&amp;L Management Fees (HT).
                  </p>
                  {(commissionPeriodTotal.charged > 0 || commissionPeriodTotal.expected > 0) && (() => {
                    const st = commissionStatus(commissionPeriodTotal.charged, commissionPeriodTotal.expected);
                    return (
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-muted-foreground tabular-nums font-mono">
                          Exp {formatCurrency(cv(commissionPeriodTotal.expected, 'GBP'), displayCurrency)}
                        </span>
                        <span className="text-sm font-semibold tabular-nums font-mono">
                          Chg {formatCurrency(cv(commissionPeriodTotal.charged, 'GBP'), displayCurrency)}
                        </span>
                        <CommissionStatusBadge status={st} cv={cv} displayCurrency={displayCurrency} />
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Region tabs + month filter */}
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Region</label>
                <div className="flex gap-1">
                  {CLEANING_REGION_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setCommissionRegion(tab.id)}
                      className={`h-9 px-3 text-sm rounded-md border ${
                        commissionRegion === tab.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-input hover:bg-muted'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Month</label>
                <select
                  value={commissionMonthFilter}
                  onChange={(e) => setCommissionMonthFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="all">All months</option>
                  {commissionDerived.monthsWithData.map((m) => {
                    const mn = parseInt(m.split('-')[1], 10);
                    return <option key={m} value={m}>{MONTHS_SHORT[mn - 1]} {year}</option>;
                  })}
                </select>
              </div>
              <button
                onClick={recomputeCommission}
                disabled={commissionRecomputing || commissionLoading}
                className="h-9 px-3 text-sm rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 flex items-center gap-1.5 self-end"
                title="Recompute all stale verification results from Smoobu bookings"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${commissionRecomputing ? 'animate-spin' : ''}`} />
                {commissionRecomputing ? 'Recomputing...' : 'Recompute'}
              </button>
            </div>

            {commissionLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : commissionGrouped.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead className="text-center">Bookings</TableHead>
                        <TableHead className="text-right" title="What CWC should charge per contract (17% of net revenue)">
                          Expected (17%)
                        </TableHead>
                        <TableHead className="text-right" title="What CWC actually billed on the statement">
                          Charged
                        </TableHead>
                        <TableHead className="text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissionGrouped.map((mg) => {
                        const mn = parseInt(mg.month.split('-')[1], 10);
                        const monthLabel = `${MONTHS_SHORT[mn - 1]} ${mg.month.split('-')[0]}`;
                        const isExpanded = expandedCommissionMonths.has(mg.month);
                        const totalBookings = mg.rows.reduce((s, r) => s + r.booking_count, 0);
                        const toggleMonth = () => {
                          setExpandedCommissionMonths((prev) => {
                            const next = new Set(prev);
                            next.has(mg.month) ? next.delete(mg.month) : next.add(mg.month);
                            return next;
                          });
                        };

                        const monthStatus = commissionStatus(mg.subtotalHt, mg.subtotalExpected);

                        return (
                          <React.Fragment key={mg.month}>
                            {/* Month summary row */}
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50 font-medium"
                              onClick={toggleMonth}
                            >
                              <TableCell className="w-8 px-2">
                                {isExpanded
                                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                }
                              </TableCell>
                              <TableCell colSpan={2}>
                                {monthLabel}
                                <span className="ml-2 text-xs text-muted-foreground font-normal">
                                  {mg.rows.length} statement{mg.rows.length !== 1 ? 's' : ''}, {totalBookings} booking{totalBookings !== 1 ? 's' : ''}
                                </span>
                              </TableCell>
                              <TableCell className="text-center tabular-nums text-muted-foreground">
                                {totalBookings}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                                {formatCurrency(cv(mg.subtotalExpected, 'GBP'), displayCurrency)}
                              </TableCell>
                              <TableCell className="text-right font-mono tabular-nums">
                                {formatCurrency(cv(mg.subtotalHt, 'GBP'), displayCurrency)}
                              </TableCell>
                              <TableCell className="text-right">
                                <CommissionStatusBadge status={monthStatus} cv={cv} displayCurrency={displayCurrency} />
                              </TableCell>
                            </TableRow>

                            {/* Expanded: per-statement rows */}
                            {isExpanded && mg.rows.map((row, ri) => {
                              const rowStatus = commissionStatus(row.amount_ht, row.expected_ht);
                              return (
                                <TableRow key={`${mg.month}-${ri}`} className="bg-muted/10 text-xs">
                                  <TableCell></TableCell>
                                  <TableCell className="py-1.5">
                                    {row.property_name}
                                  </TableCell>
                                  <TableCell className="py-1.5 text-muted-foreground">
                                    {row.entity_name}
                                    {row.agency && (
                                      <span className="ml-1 text-[10px] opacity-60">({row.agency})</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center py-1.5 tabular-nums">
                                    {row.booking_count}
                                  </TableCell>
                                  <TableCell className="text-right font-mono py-1.5 tabular-nums text-muted-foreground">
                                    {formatCurrency(cv(row.expected_ht, 'GBP'), displayCurrency)}
                                  </TableCell>
                                  <TableCell className="text-right font-mono py-1.5 tabular-nums">
                                    {formatCurrency(cv(row.amount_ht, 'GBP'), displayCurrency)}
                                  </TableCell>
                                  <TableCell className="text-right py-1.5">
                                    <CommissionStatusBadge status={rowStatus} cv={cv} displayCurrency={displayCurrency} size="sm" />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground text-sm">
                    {commissionRegion !== 'all'
                      ? `No management fee data for ${commissionRegion === 'uk' ? 'UK' : 'France'} properties in ${year}.`
                      : `No management fee data across portfolio properties in ${year}.`
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ═══════════════ MAINTENANCE TAB ═══════════════ */}
          <TabsContent value="maintenance" className="space-y-6">

            {/* Scope header + period total */}
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm text-muted-foreground">
                    Maintenance expenses extracted from management statements. Amounts enter P&amp;L Operational costs.
                  </p>
                  {maintenancePeriodTotal > 0 && (
                    <span className="text-sm font-semibold tabular-nums font-mono shrink-0">
                      Total {formatCurrency(cv(maintenancePeriodTotal, 'GBP'), displayCurrency)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Region tabs + month filter + property filter + reconcile */}
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Region</label>
                <div className="flex gap-1">
                  {CLEANING_REGION_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setMaintenanceRegion(tab.id)}
                      className={`h-9 px-3 text-sm rounded-md border ${
                        maintenanceRegion === tab.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-input hover:bg-muted'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Month</label>
                <select
                  value={maintenanceMonthFilter}
                  onChange={(e) => setMaintenanceMonthFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="all">All months</option>
                  {maintenanceDerived.monthsWithData.map((m) => {
                    const mn = parseInt(m.split('-')[1], 10);
                    return <option key={m} value={m}>{MONTHS_SHORT[mn - 1]} {year}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Property</label>
                <select
                  value={maintenancePropertyFilter}
                  onChange={(e) => setMaintenancePropertyFilter(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="all">All properties</option>
                  {maintenanceDerived.properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={recomputeMaintenance}
                disabled={maintenanceRecomputing || maintenanceLoading}
                className="h-9 px-3 text-sm rounded-md border border-input bg-background hover:bg-muted disabled:opacity-50 flex items-center gap-1.5 self-end"
                title="Recompute all maintenance verification results from management statements"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${maintenanceRecomputing ? 'animate-spin' : ''}`} />
                {maintenanceRecomputing ? 'Recomputing...' : 'Recompute'}
              </button>
            </div>

            {maintenanceLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : maintenanceGrouped.length > 0 ? (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Property</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {maintenanceGrouped.map((mg) => {
                        const mn = parseInt(mg.month.split('-')[1], 10);
                        const monthLabel = `${MONTHS_SHORT[mn - 1]} ${mg.month.split('-')[0]}`;
                        const isExpanded = expandedMaintenanceMonths.has(mg.month);
                        const toggleMonth = () => {
                          setExpandedMaintenanceMonths((prev) => {
                            const next = new Set(prev);
                            next.has(mg.month) ? next.delete(mg.month) : next.add(mg.month);
                            return next;
                          });
                        };

                        return (
                          <React.Fragment key={mg.month}>
                            {/* Month summary row */}
                            <TableRow
                              className="cursor-pointer hover:bg-muted/50 font-medium"
                              onClick={toggleMonth}
                            >
                              <TableCell className="w-8 px-2">
                                {isExpanded
                                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                }
                              </TableCell>
                              <TableCell colSpan={3}>
                                {monthLabel}
                                <span className="ml-2 text-xs text-muted-foreground font-normal">
                                  {mg.lineCount} expense{mg.lineCount !== 1 ? 's' : ''} across {mg.propGroups.length} propert{mg.propGroups.length !== 1 ? 'ies' : 'y'}
                                </span>
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell className="text-right font-mono tabular-nums">
                                {formatCurrency(cv(mg.subtotal, 'GBP'), displayCurrency)}
                              </TableCell>
                            </TableRow>

                            {/* Expanded: per-property groups with individual lines */}
                            {isExpanded && mg.propGroups.map((pg) => (
                              <React.Fragment key={`${mg.month}-${pg.property_ref}`}>
                                {/* Property sub-header */}
                                <TableRow className="bg-muted/20">
                                  <TableCell></TableCell>
                                  <TableCell className="py-1.5 font-medium text-xs" colSpan={3}>
                                    {pg.property_name}
                                    <span className="ml-2 text-muted-foreground font-normal">
                                      {pg.rows.length} line{pg.rows.length !== 1 ? 's' : ''}
                                    </span>
                                  </TableCell>
                                  <TableCell></TableCell>
                                  <TableCell className="text-right font-mono py-1.5 tabular-nums text-xs font-medium">
                                    {formatCurrency(cv(pg.rows.reduce((s, r) => s + r.amount, 0), 'GBP'), displayCurrency)}
                                  </TableCell>
                                </TableRow>
                                {/* Individual expense lines */}
                                {pg.rows.map((row, ri) => (
                                  <TableRow key={`${mg.month}-${pg.property_ref}-${ri}`} className="bg-muted/10 text-xs">
                                    <TableCell></TableCell>
                                    <TableCell className="py-1.5 text-muted-foreground">
                                      {row.date || '-'}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-muted-foreground">
                                      {row.entity_name}
                                      {row.agency && (
                                        <span className="ml-1 text-[10px] opacity-60">({row.agency})</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-1.5">
                                      {row.supplier || '-'}
                                    </TableCell>
                                    <TableCell className="py-1.5 text-muted-foreground max-w-[200px] truncate" title={row.description}>
                                      {row.description || '-'}
                                    </TableCell>
                                    <TableCell className="text-right font-mono py-1.5 tabular-nums">
                                      {formatCurrency(cv(row.amount, 'GBP'), displayCurrency)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground text-sm">
                    {maintenanceRegion !== 'all'
                      ? `No maintenance expenses for ${maintenanceRegion === 'uk' ? 'UK' : 'France'} properties in ${year}.`
                      : `No maintenance data across portfolio properties in ${year}.`
                    }
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ═══════════════ DETECTED RECURRING COSTS TAB ═══════════════ */}
          <TabsContent value="detected" className="space-y-6">
            <DetectedRecurringCosts eurGbpRate={eurGbpRate} />
          </TabsContent>

          {/* ═══════════════ MONTHLY OUTGOINGS TAB ═══════════════ */}
          <TabsContent value="outgoings" className="space-y-6">
            <MonthlyOutgoings eurGbpRate={eurGbpRate} entities={outgoingsEntities} />
          </TabsContent>

        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// ── Cleaning tab sub-components ──

function CleanTypeBadge({ classification }) {
  const styles = {
    turnover_clean: 'text-green-600',
    non_turnover: 'text-blue-500',
    unparseable: 'text-red-500',
  };
  const labels = {
    turnover_clean: 'Turnover',
    non_turnover: 'Non-turnover',
    unparseable: 'Unparseable',
  };
  return (
    <span className={`text-xs font-medium ${styles[classification] || 'text-gray-500'}`}>
      {labels[classification] || classification}
    </span>
  );
}

function CleanMatchCell({ row }) {
  if (row.classification === 'unparseable') {
    return <span className="text-red-500 text-xs">Date not parsed</span>;
  }

  if (row.classification === 'non_turnover') {
    return (
      <span className="text-blue-500 text-xs">
        Non-turnover (no checkout expected)
        {row.non_turnover_reason && (
          <span className="text-muted-foreground ml-1">({row.non_turnover_reason})</span>
        )}
      </span>
    );
  }

  // turnover_clean
  if (row.match_outcome === 'verified' && row.matched_checkouts?.[0]) {
    const co = row.matched_checkouts[0];
    return (
      <span className="font-mono text-green-700 dark:text-green-400 text-xs">
        {co.guest_name}, checkout {co.check_out}
        <span className="text-muted-foreground ml-1">(#{co.smoobu_id})</span>
      </span>
    );
  }

  if (row.match_outcome === 'ambiguous') {
    return (
      <span className="text-orange-500 text-xs">
        Ambiguous ({row.matched_checkouts?.length || 0} candidates)
      </span>
    );
  }

  if (row.match_outcome === 'no_checkout') {
    return <span className="text-yellow-600 text-xs">Turnover, no checkout matched</span>;
  }

  return <span className="text-muted-foreground text-xs">-</span>;
}

// ── Commission tab sub-components ──

function CommissionStatusBadge({ status, cv, displayCurrency, size }) {
  const { label, gap, variant } = status;
  const style = STATUS_BADGE_STYLES[variant] || STATUS_BADGE_STYLES.neutral;
  const textSize = size === 'sm' ? 'text-[10px]' : 'text-xs';
  if (variant === 'neutral') {
    return <span className={`${textSize} text-muted-foreground`}>{label}</span>;
  }
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${textSize} ${style}`}>
      {label}
      {gap > 0 && (
        <span className="font-mono tabular-nums">
          {formatCurrency(cv(gap, 'GBP'), displayCurrency)}
        </span>
      )}
    </span>
  );
}
